import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'dexie';
import { finalize, take } from 'rxjs';
import { APIResponse, Utils, AppData, CompanyFieldsUtils } from 'fakturnia-shared';
import { NotificationData } from 'fakturnia-shared';
import { DataSynchronizerService } from 'shared/services/data-synchronization.service';
import { SynchronizationService } from 'shared/services/synchronization.service';
import { ToastService } from 'shared/services/toast.service';
import { cloneDeep} from 'lodash'
import { Company } from 'fakturnia-shared';
import { ClientsApiService } from 'shared/services/api/clients.api.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SnackbarService } from 'shared/services/snackbar.service';
import { NumericOnlyDirective } from 'shared/directives/number-only.directive';
import { noWhitespaceValidator } from 'shared/validators/no-whitespace.validator';

@Component({
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule, MatIconModule, ReactiveFormsModule, FormsModule, MatDialogModule, MatTooltipModule, NumericOnlyDirective],
  selector: 'app-create-or-update-client-dialog',
  templateUrl: './create-or-update-client-dialog.component.html',
  styleUrls: ['./create-or-update-client-dialog.component.scss']
})
export class CreateOrUpdateClientDialogComponent implements OnInit, OnDestroy{

  private _subscriptions: Subscription[] = []

  loading = false

  clientType: 'seller' | 'buyer' | 'receiver'
  client = null

  mode: 'create' | 'update' = 'create'

  // Lista krajów z AppData
  countries = AppData.Countries;
  euCountries = AppData.Countries.filter(c => AppData.isEUCountry(c.code));

  // Pola, które można przełączać w sekcji "Widoczność pól na fakturze"
  toggleableFields: { key: string; label: string }[] = [
    { key: 'vatUE', label: 'VAT UE' },
    { key: 'otherTaxIdentifier', label: 'Identyfikator podatkowy' },
    { key: 'pesel', label: 'PESEL' },
    { key: 'idNumber', label: 'Numer dowodu' },
    { key: 'regon', label: 'REGON' },
    { key: 'bdo', label: 'BDO' },
    { key: 'krs', label: 'KRS' },
    { key: 'description', label: 'Pole opisowe' },
    { key: 'phoneNumber', label: 'Telefon' },
    { key: 'fax', label: 'Fax' },
    { key: 'email', label: 'E-mail' },
    { key: 'www', label: 'Strona WWW' },
  ];

  // Opcje typu identyfikatora
  identifierTypeOptions = [
    { value: 'nip', label: 'NIP' },
    { value: 'vatEu', label: 'Numer VAT-UE' },
    { value: 'other', label: 'Identyfikator podatkowy inny' },
    { value: 'idWew', label: 'Identyfikator wewnętrzny' },
    { value: 'none', label: 'Brak identyfikatora' },
  ];
  
  // Field names must match
  form = new FormGroup({
    _id: new FormControl(''),
    type: new FormControl('company', [Validators.required]),
    name: new FormControl('', [Validators.required, noWhitespaceValidator()]),
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    phoneNumber: new FormControl(''),
    email: new FormControl(''),
    nip: new FormControl(''),
    vatUE: new FormControl(''),
    identifierType: new FormControl('nip'),
    otherTaxIdentifier: new FormControl(''),
    idWew: new FormControl(''),
    regon: new FormControl(''),
    street: new FormControl('', [Validators.required, noWhitespaceValidator()]),
    postalCode: new FormControl('', [Validators.required, noWhitespaceValidator()]),
    city: new FormControl('', [Validators.required, noWhitespaceValidator()]),
    country: new FormControl(''),
    countryCode: new FormControl('PL', [Validators.required]),  // NOWE!
    pesel: new FormControl(''),
    idNumber: new FormControl(''),
    bdo: new FormControl(''),
    krs: new FormControl(''),
    www: new FormControl(''),
    fax: new FormControl(''),
    description: new FormControl(''),
  })

  fieldsToShow = new Set<string>()

  constructor(
    private _clientsApiService:ClientsApiService,
    private _dialog: MatDialogRef<CreateOrUpdateClientDialogComponent>,
    private _dataSynchronizerService:DataSynchronizerService,
    private _synchronizationService:SynchronizationService,
    private _snackbarService:SnackbarService,
    private _cdr:ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: {clientType: 'seller' | 'buyer'  | 'receiver', client: Company}) {

     
      this.form.controls.type.setValue('company')
      if (typeof data == 'undefined') return
      if(data.client == null) return
      if(typeof data.client == 'undefined') return
      if(typeof data.clientType != 'undefined') {
        this.clientType = data.clientType
      } else this.clientType = 'buyer'

      // Set fields to show - normalizuj legacy klucze ('telefon','PESEL',...) na nowy format
      if(Utils.isDefined(data,"client.fieldsToShow") && Array.isArray(data.client.fieldsToShow)) {
        this.fieldsToShow = new Set<string>(CompanyFieldsUtils.normalize(data.client.fieldsToShow))
      } else this.fieldsToShow = new Set<string>([])

      this.mode = data.client._id == null ? 'create' : 'update'
    
      Object.keys(this.form.controls).forEach(field=> {
        this.form.controls[field].setValue(data.client[field])
      })

      // Ustaw countryCode (backward compatibility)
      const countryCode = (data.client as any).countryCode || 
                          AppData.getCountryCodeByName(data.client.country) || 
                          'PL';
      this.form.controls.countryCode.setValue(countryCode);

      // Ustaw identifierType - jawnie ustawiony lub auto-detekcja
      const identifierType = this._resolveIdentifierType(data.client, countryCode);
      this.form.controls.identifierType.setValue(identifierType);
      this._applyIdentifierTypeRules(identifierType);

  }

  /**
   * Określa typ identyfikatora na podstawie danych klienta (backward compatibility)
   */
  private _resolveIdentifierType(client: any, countryCode: string): string {
    if (client.identifierType && ['nip', 'vatEu', 'other', 'none', 'idWew'].includes(client.identifierType)) {
      return client.identifierType;
    }
    const isPolish = countryCode === 'PL';
    const isEU = AppData.isEUCountry(countryCode);
    if (isPolish && client.nip) return 'nip';
    if (!isPolish && isEU && client.vatUE) return 'vatEu';
    if (!isPolish && !isEU && (client.nip || client.otherTaxIdentifier || client.idNumber)) return 'other';
    if (client.nip) return 'nip';
    if (client.type === 'person' && !client.nip) return 'none';
    return 'nip';
  }

  /**
   * Wymusza widoczność pól na fakturze w zależności od identifierType
   */
  private _applyIdentifierTypeRules(identifierType: string) {
    if (identifierType === 'vatEu') {
      this.fieldsToShow.add('vatUE');
      this.fieldsToShow.delete('otherTaxIdentifier');
    } else if (identifierType === 'other') {
      this.fieldsToShow.add('otherTaxIdentifier');
      this.fieldsToShow.delete('vatUE');
    } else {
      this.fieldsToShow.delete('vatUE');
      this.fieldsToShow.delete('otherTaxIdentifier');
    }

    // Wymagaj pola NIP gdy wybrany identifierType to 'nip', w pozostałych przypadkach usuń walidator
    if (identifierType === 'nip') {
      this.form.controls.nip.setValidators([Validators.required, noWhitespaceValidator()]);
    } else {
      this.form.controls.nip.clearValidators();
    }
    this.form.controls.nip.updateValueAndValidity();
  }

  /**
   * Obsługa zmiany typu identyfikatora
   */
  onIdentifierTypeChange(identifierType: string): void {
    this._applyIdentifierTypeRules(identifierType);
  }

  public toggleFieldVisibility(field) {
    // Nie pozwalaj wyłączyć pola, które jest zablokowane przez identifierType
    if (this.isFieldLocked(field)) return;
    // Użyj utils - przy wyłączaniu usuwa też legacy odpowiedniki (np. 'telefon' gdy wyłączamy 'phoneNumber'),
    // dzięki czemu PDF nie pokaże już tego pola.
    const next = CompanyFieldsUtils.toggle(this.fieldsToShow, field);
    this.fieldsToShow = new Set<string>(next);
  }

  /**
   * Sprawdza czy pole jest zablokowane przez aktualny identifierType
   */
  isFieldLocked(field: string): boolean {
    const idType = this.form.controls.identifierType.value;
    if (field === 'vatUE' && idType === 'vatEu') return true;
    if (field === 'otherTaxIdentifier' && idType === 'other') return true;
    return false;
  }

  /**
   * Obsługa zmiany kraju - automatyczne ustawienie country (nazwa)
   * oraz auto-włączenie pola VAT UE dla krajów != PL
   */
  onCountryChange(countryCode: string): void {
    const countryName = AppData.getCountryNameByCode(countryCode) || '';
    this.form.controls.country.setValue(countryName);

    // Dla krajów != PL automatycznie włącz widoczność VAT UE
    if (countryCode !== 'PL') {
      this.fieldsToShow.add('vatUE');
    }
  }

  ngOnInit(): void {
    this._subscriptions.push(this.form.controls.type.valueChanges.subscribe({
      next: (type) => this._applyTypeValidators(type)
    }))

    // Zastosuj walidatory dla aktualnego typu (np. gdy dane załadowane w konstruktorze)
    this._applyTypeValidators(this.form.controls.type.value);

    // Pokaż błędy od razu dla pól, które są już niepoprawne (puste/spacje)
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.controls[key];
      if (control.invalid) {
        control.markAsTouched();
      }
    });
  }

  private _applyTypeValidators(type: string | null): void {
    if (type === 'person') {
      this.form.controls.name.setValidators(null)
      this.form.controls.name.updateValueAndValidity()

      this.form.controls.firstName.setValidators([Validators.required, noWhitespaceValidator()])
      this.form.controls.firstName.updateValueAndValidity()

      this.form.controls.lastName.setValidators([Validators.required, noWhitespaceValidator()])
      this.form.controls.lastName.updateValueAndValidity()
    } else {
      this.form.controls.firstName.setValidators(null)
      this.form.controls.firstName.updateValueAndValidity()

      this.form.controls.lastName.setValidators(null)
      this.form.controls.lastName.updateValueAndValidity()

      this.form.controls.name.setValidators(Validators.required)
      this.form.controls.name.updateValueAndValidity()
    }

    this.form.updateValueAndValidity()
    this._cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub=>sub.unsubscribe())
  }

  saveChanges() {
    this.form.markAllAsTouched();
    if(!this.form.valid) { return}
    if(this.loading == true) return
    if(this.clientType == 'seller') {

      this.loading = true
      let company:any = cloneDeep(this.form.getRawValue())
      company.fieldsToShow = Array.from(this.fieldsToShow)
      delete company._id
      this._dialog.close({
        success: true,
        client: company
      })
      this.loading = false
      return 
    }

    const requestData:any = cloneDeep(this.form.getRawValue())
    requestData.fieldsToShow = Array.from(this.fieldsToShow)

    if(this.mode == 'create') {
      this.loading = true
      this._clientsApiService.create(requestData)
      .pipe(finalize(() => {
        this.loading = false
      }))
      .pipe(take(1))
      .subscribe({
          next: (response:APIResponse) => this.processResponse(response)
      })
    }

    else if(this.mode == 'update') {
      this.loading = true
      const id = requestData._id
      delete requestData._id
      this._clientsApiService.update(id,requestData)
      .pipe(finalize(() => {
        this.loading = false
      }))
      .pipe(take(1))
      .subscribe({
          next: (response:APIResponse) => this.processResponse(response)
      })
    }
    
  }

  processResponse = (response:APIResponse) => {
    if(response.success != true) {
      this._snackbarService.showSnackBar(response.message,'error')
      return
    }

    this._dataSynchronizerService.next('clients',[response.data], null)
    this._synchronizationService.synchronize()
    this._dialog.close({
      success: true,
      client: response.data
    })

    this._snackbarService.showSnackBar(
      "Zapisano zmiany",
      "success", 
      4000
    )
  }

  close() {
    this._dialog.close()
  }
}
